import React, { useState } from "react";
import { X, ExternalLink, Upload } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import FvtCheckoutButton from "./FvtCheckoutButton";

const TASK_EMOJIS = {
  buy_fvt: "🎟️",
  likes_on_repost: "⭐",
};

const TASK_COLORS = {
  buy_fvt: "#c8f135",
  likes_on_repost: "#60a5fa",
};

const TASK_COPY = {
  buy_fvt: {
    textLabel: "מספר הזמנה",
    textPlaceholder: "הזן מספר הזמנה",
    fileLabel: "תמונת קבלה",
    helper: "העלה צילום מסך או קבלה כדי שנוכל לבדוק את הרכישה.",
  },
  likes_on_repost: {
    textLabel: "קישור לפוסט",
    textPlaceholder: "https://x.com/...",
    fileLabel: "צילום מסך אנליטיקות",
    helper: "העלה צילום מסך של האנליטיקות עבור הפוסט שלך.",
  },
};

export default function ManualReviewSubmissionModal({ task, submitting, onClose, onSubmit }) {
  const [referenceText, setReferenceText] = useState("");
  const [file, setFile] = useState(null);

  const emoji = TASK_EMOJIS[task.task_type] || "✅";
  const color = TASK_COLORS[task.task_type] || "#c8f135";
  const copy = TASK_COPY[task.task_type] || TASK_COPY.buy_fvt;
  const isFvt = task.task_type === "buy_fvt";
  const canSubmit = referenceText.trim() && file;

  return (
    <AnimatePresence>
      <motion.div
        key="overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
        style={{ background: "rgba(0,0,0,0.88)", backdropFilter: "blur(8px)" }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          key="modal"
          initial={{ y: 60, opacity: 0, scale: 0.97 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 60, opacity: 0, scale: 0.97 }}
          transition={{ type: "spring", damping: 22, stiffness: 280 }}
          className="w-full max-w-md rounded-3xl overflow-hidden"
          style={{ background: "#111111", border: `1px solid ${color}30` }}
        >
          <div className="h-1 w-full" style={{ background: `linear-gradient(90deg, ${color}, ${color}80)` }} />

          <div className="p-6 pb-4" style={{ borderBottom: "1px solid #1a1a1a" }}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{emoji}</span>
                <h2 className="font-black text-white text-lg leading-tight" dir="rtl">{task.title}</h2>
              </div>
              <button onClick={onClose} className="p-1 flex-shrink-0 hover:opacity-70 transition-opacity">
                <X className="w-5 h-5" style={{ color: "rgba(255,255,255,0.4)" }} />
              </button>
            </div>
          </div>

          <div className="p-6" dir="rtl">
            <p className="text-sm leading-relaxed mb-5" style={{ color: "rgba(255,255,255,0.55)" }}>
              {task.description}
            </p>

            {isFvt ? (
              /* ── Stripe checkout for FVT ── */
              <>
                <div className="rounded-xl p-4 mb-5" style={{ background: `${color}10`, border: `1px solid ${color}30` }}>
                  <p className="text-xs font-bold mb-1" style={{ color }}>רכישה מאובטחת</p>
                  <p className="text-sm" style={{ color: "rgba(255,255,255,0.65)" }}>
                    לאחר הרכישה נקודות יזוכו לחשבונך אוטומטית לאחר אימות.
                  </p>
                </div>
                <div className="rounded-xl p-3 mb-5 flex items-center justify-between" style={{ background: "#1a1a1a", border: "1px solid #2a2a2a" }}>
                  <span className="text-sm font-bold text-white">FVT — Fan VIP Ticket</span>
                  <span className="font-black text-xl" style={{ color }}>$199</span>
                </div>
                <FvtCheckoutButton color={color} />
              </>
            ) : (
              /* ── Manual review for likes_on_repost ── */
              <>
                <div className="rounded-xl p-3 mb-5" style={{ background: `${color}10`, border: `1px solid ${color}30` }}>
                  <p className="text-xs font-bold mb-1" style={{ color }}>בדיקה ידנית</p>
                  <p className="text-sm" style={{ color: "rgba(255,255,255,0.65)" }}>{copy.helper}</p>
                </div>

                {task.action_url && (
                  <a
                    href={task.action_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-bold text-sm mb-4 transition-all hover:bg-[#222]"
                    style={{ background: "#1a1a1a", color: "rgba(255,255,255,0.7)", border: "1px solid #2a2a2a" }}
                  >
                    פתח קישור למשימה
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}

                <div className="mb-4">
                  <label className="block text-xs font-bold mb-2" style={{ color: "rgba(255,255,255,0.4)" }}>
                    {copy.textLabel}
                  </label>
                  <input
                    type="url"
                    value={referenceText}
                    onChange={(e) => setReferenceText(e.target.value)}
                    placeholder={copy.textPlaceholder}
                    className="w-full rounded-xl px-4 py-3 text-sm font-medium outline-none"
                    style={{ background: "#1a1a1a", border: `1px solid ${referenceText ? color + "60" : "#2a2a2a"}`, color: "#fff", transition: "border-color 0.2s" }}
                  />
                </div>

                <div className="mb-5">
                  <label className="block text-xs font-bold mb-2" style={{ color: "rgba(255,255,255,0.4)" }}>
                    {copy.fileLabel}
                  </label>
                  <label
                    className="flex items-center justify-center gap-3 rounded-xl px-4 py-4 cursor-pointer"
                    style={{ background: "#1a1a1a", border: `1px dashed ${file ? color : "#2a2a2a"}`, color: file ? "#fff" : "rgba(255,255,255,0.45)" }}
                  >
                    <Upload className="w-4 h-4" />
                    <span className="text-sm font-medium">{file ? file.name : "בחר קובץ תמונה"}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                    />
                  </label>
                </div>

                <motion.button
                  onClick={() => onSubmit(task, { reference_text: referenceText.trim(), file })}
                  disabled={!canSubmit || submitting}
                  whileTap={{ scale: canSubmit && !submitting ? 0.96 : 1 }}
                  className="w-full py-4 rounded-xl font-black text-base transition-all disabled:opacity-40"
                  style={{
                    background: canSubmit && !submitting ? color : "#2a2a2a",
                    color: canSubmit && !submitting ? (color === "#c8f135" ? "#000" : "#fff") : "rgba(255,255,255,0.3)",
                  }}
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-transparent rounded-full animate-spin" style={{ borderTopColor: "currentColor" }} />
                      שולח לבדיקה...
                    </span>
                  ) : "שלח לבדיקה"}
                </motion.button>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}